/* eslint-disable jsx-a11y/anchor-is-valid */

import React, {useEffect, useState} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {PageLink} from '../../../../_metronic/layout/core'
import db from '../../../../services/firebase'
import {collection, deleteDoc, doc, onSnapshot, updateDoc} from 'firebase/firestore'
import {VendorPageTitle} from '../../../../_metronic/layout/core/VendorPageData'
import {useAuth} from '../../../modules/auth'
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Searchbar from 'app/components/Searchbar'
import OwnerDisplay from 'app/components/OwnerDisplay'

type Props = {}

const Archive: React.FC<Props> = () => {
  const {currentUser} = useAuth()
  const [vendor, setVendor] = useState<any[]>([])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMultiDeleteModalOpen, setIsMultiDeleteModalOpen] = useState(false)
  const [isMultiDeleting, setIsMultiDeleting] = useState(false)

  useEffect(() => {
    getVendor()
  }, [])

  useEffect(() => {
    setFilteredItems(vendor)
  }, [vendor])

  useEffect(() => {
    // Delay the rendering of the empty state for 3 seconds
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  const getVendor = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'vendors')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            archived: doc.data().archived,
          }))
          .filter((vendor) => vendor.archived) 
        setVendor(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(()=>{
    if (searchTerm === '') {
      setFilteredItems(vendor)
      setSearchLength(false)
    } else {
      const filteredItems = vendor.filter((user) =>
        (user.vendor_name || '').toLowerCase().includes(searchTerm)
      )
      setFilteredItems(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  },[searchTerm,vendor])

  const usersBreadcrumbs: Array<PageLink> = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ]

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleArchive = async (id) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const vendorsRef = collection(tenantRef, 'vendors')
      const docRef = doc(vendorsRef, id)

      await updateDoc(docRef, {
        archived: false,
      })
      setVendor((prevVendors) => prevVendors.map((v) => (v.id === id ? {...v, archived: true} : v)))
      showSuccessToast({message: 'Vendor has been removed from archived.'})
    } catch (error) {
      showErrorToast({message: 'An error occurred while  unarchiving the vendor.'})
    }
  }

  const deleteHandler = async (Data) => {
    setSelectedVendor(Data)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'vendors')
      const docRef = doc(policyRef, selectedVendor.id)
      await deleteDoc(docRef)
      getVendor()
      showSuccessToast({message:`"${selectedVendor.vendorName}" has been deleted.`})
    } catch (error) {
      showErrorToast({message:'An error occurred while deleting the vendor.'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
    }
  }

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked
    setSelectAll(isChecked)
    setCheckedItems(isChecked ? filteredItems.map((item) => item.id) : [])
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => {
      const newCheckedItems = checked ? [...prev, value] : prev.filter((item) => item !== value)

      setSelectAll(newCheckedItems.length === filteredItems.length)

      return newCheckedItems
    })
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  const handleMultipleDelete = async () => {
    setIsMultiDeleteModalOpen(true)
  }
  const handleConfirmMultiDelete = async () => {
    try {
      setIsMultiDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'vendors')

      const deletePromises = checkedItems.map(async (vendorId) => {
        const docRef = doc(policyRef, vendorId)
        await deleteDoc(docRef)
      })

      await Promise.all(deletePromises)
      getVendor()
      showSuccessToast({message: 'Selected vendors have been deleted successfully!'})

      setCheckedItems([])
      setSelectAll(false)
    } catch (error) {
      showErrorToast({message: 'Failed to delete vendors'})
    } finally {
      setIsMultiDeleting(false)
      setIsMultiDeleteModalOpen(false)
    }
  }

  return (
    <div>
      <VendorPageTitle
        icon='VendorIcon.svg'
        title='Vendor Risk Management'
        desc={
          'Monitor risks associated with third party services to enhance your security posture. '
        }
        breadcrumbs={usersBreadcrumbs}
      />

      <div className='card card-custom '>
        <div className='card-header border-0 pt-6 h-100'>
          <div className='card-title'>
          <div className='flex-lg-fill'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          </div>
          <div className='card-toolbar'>
            <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
              {checkedItems.length > 0 ? (
                <>
                  {checkedItems.length > 0 ? (
                    <div className='  border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                      <span>
                        <p className='pt-4'>{checkedItems.length} Selected</p>
                      </span>
                    </div>
                  ) : null}
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                      <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                    </div>

                    <button
                      className='btn btn-sm fs-6'
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#148363')
                      }
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#0E9F6E')
                      }
                      onClick={handleMultipleDelete}
                      style={{
                        backgroundColor: '#0E9F6E',
                        color: '#fff',
                        width: '140px',
                        height: '40px',
                        borderRadius: '4px',
                        left: '615px',
                        top: '20px',
                        transition: 'background-color 0.3s',
                      }}
                    >
                      {' '}
                      Delete vendors
                    </button>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
        <div className='table-responsive py-3 px-10' style={{minHeight: '50vh'}}>
          {/* begin::Table */}
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            {/* begin::Table head */}
            <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='w-25px'>
                  <div className='form-check form-check-sm form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={selectAll}
                      data-kt-check='true'
                      data-kt-check-target='.widget-9-check'
                      onChange={handleSelectAllChange}
                    />
                  </div>
                </th>
                <th className='min-w-300px'>VENDOR NAME</th>
                <th className='min-w-150px'>RISK LEVEL</th>
                <th className='min-w-150px'>DUE DILIGENCE</th>
                <th className='min-w-150px'>CATEGORY</th>
                <th className='min-w-150px'>OWNER</th>
                <th className='min-w-50px'></th>
              </tr>
            </thead>
            {/* end::Table head */}
            {/* begin::Table body */}
            <tbody>
              {filteredItems && filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <tr>
                    <td>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input widget-9-check'
                          type='checkbox'
                          value={item.id}
                          checked={checkedItems.includes(item.id)}
                          onChange={handleCheckChange}
                        />
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='symbol symbol-30px me-3'>
                          {/* <img src={toAbsoluteUrl(item.logo[0].url)} alt='' loading='lazy' /> */}
                          {item.logo && item.logo.length > 0 ? (
                            <img src={toAbsoluteUrl(item.logo[0]?.url)} alt='' loading='lazy' />
                          ) : (
                            <img src={toAbsoluteUrl(item.logoUrl)} alt='' loading='lazy' />
                          )}
                        </div>
                        <div className='d-flex justify-content-start flex-column'>
                          <a
                            href={`/vendor-risk/details/${item.id}`}
                            className='text-dark fw-bold text-hover-primary fs-6'
                          >
                            {item.vendor_name}
                          </a>
                        </div>
                      </div>
                    </td>

                    <td>
                      {item.risk_level ? (
                        <a
                          href='#'
                          className={`badge fs-7 ${
                            item.risk_level === 'High'
                              ? 'badge-danger'
                              : item.risk_level === 'Medium'
                              ? 'badge-warning'
                              : 'badge-primary'
                          }`}
                          style={{
                            color:
                              item.risk_level === 'High'
                                ? '#EE3043'
                                : item.risk_level === 'Medium'
                                ? '#FFA800'
                                : '#0E9F6E',
                            background:
                              item.risk_level === 'High'
                                ? '#FFF5F8'
                                : item.risk_level === 'Medium'
                                ? '#FFF8DD'
                                : '#E8F8F4',
                          }}
                        >
                          {item.risk_level}
                        </a>
                      ) : (
                        '--'
                      )}
                    </td>

                    <td>
                      <div className='d-flex flex-column w-100 me-2'>
                        <div className='d-flex flex-stack mb-2'>
                          <div className='clearfix bshadow0 pbs fw-semibold'>
                            <span
                              className={`badge fs-7 ${
                                !item.due_diligence_status ||
                                item.due_diligence_status === 'Incomplete'
                                  ? 'badge-danger'
                                  : 'badge-primary'
                              }`}
                              style={{
                                color:
                                  !item.due_diligence_status ||
                                  item.due_diligence_status === 'Incomplete'
                                    ? '#EE3043'
                                    : '#0E9F6E',
                                background:
                                  !item.due_diligence_status ||
                                  item.due_diligence_status === 'Incomplete'
                                    ? '#FFF5F8'
                                    : '#E8F8F4',
                              }}
                            >
                              {item.due_diligence_status ? item.due_diligence_status : 'Incomplete'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='d-flex justify-content-start flex-column'>
                          {item.category ? (
                            <a href='#' className='text-dark fw-bold text-hover-primary fs-6'>
                              {item.category}
                            </a>
                          ) : (
                            '--'
                          )}
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className='d-flex align-items-center'>
                        {item.owner ? (
                            <OwnerDisplay owner={item.owner} />
                        ) : (
                          <span>--</span>
                        )}
                      </div>
                    </td>
                    <td className=''>
                      <div className='dropdown mx-2'>
                        <button
                          className='btn btn-secondary border-0 p-0 m-2'
                          type='button'
                          id='dropdownMenu2'
                          data-bs-toggle='dropdown'
                          aria-haspopup='true'
                          aria-expanded='false'
                        >
                          <img
                            src='/media/buttons/Options.svg'
                            alt='Options'
                            onClick={toggleDropdown}
                          />
                        </button>
                        <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                          <li>
                            <button
                              className='dropdown-item text-hover-primary'
                              type='button'
                              style={{color: '#262626'}}
                              onClick={() => handleArchive(item.id)}
                              disabled={currentUser?.role === 'Auditor'}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                stroke-width='2'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-archive  mx-1 mb-1'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M3 4m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z' />
                                <path d='M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-10' />
                                <path d='M10 12l4 0' />
                              </svg>{' '}
                              Unarchive Vendor
                            </button>
                          </li>

                          <li>
                            <button
                              className='dropdown-item'
                              type='button'
                              style={{color: '#F15363'}}
                              onClick={(e) => deleteHandler(item)}
                              disabled={currentUser?.role === 'Auditor'}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='#F15363'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M4 7l16 0' />
                                <path d='M10 11l0 6' />
                                <path d='M14 11l0 6' />
                                <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                              </svg>{' '}
                              Delete Vendor
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <>
                  <tr style={{height: '100%'}}>
                    {searchLength ? (
                      <td colSpan={10}>
                        <div
                          className='d-flex justify-content-center align-items-center text-center'
                          style={{height: '100%'}}
                        >
                          <div className='m-20'>
                            <img src={toAbsoluteUrl('/media/emptystate/riskstate.svg')} alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No vendor found!</p>
                            </div>
                            <p>
                              We couldn't find any vendor that match your search. Maybe try a
                              different search!
                            </p>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <td colSpan={10}>
                        <div
                          className='d-flex justify-content-center align-items-center text-center'
                          style={{height: '100%'}}
                        >
                          {!showEmptyState ? (
                            <div className='m-20'>{/* <RiskListLoading /> */}</div>
                          ) : (
                            <div className='m-20'>
                              <img src={toAbsoluteUrl('/media/emptystate/riskstate.svg')} alt='' />
                              <div className='m-4 fs-5'>
                                <p style={{fontWeight: 'bold'}}>No vendors archived</p>
                              </div>
                              <p>We couldn't find any vendors that has been archived.</p>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                </>
              )}
            </tbody>
            {/* end::Table body */}
          </table>
          {/* end::Table */}
        </div>
      </div>
      {/* begin::Table container */}

      {/* begin::Body */}
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => setIsDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedVendor?.vendorName}
        isLoading={isDeleting}
        title="Delete Vendor"
        message={`Are you sure you want to delete ${selectedVendor?.vendorName}? This action cannot be undone.`}
        type="delete"
      />
      <ConfirmationModal
        isModalOpen={isMultiDeleteModalOpen}
        hideModal={() => setIsMultiDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmMultiDelete}
        toDeleteItemTitle={`${checkedItems.length} vendors`}
        isLoading={isMultiDeleting}
        title="Delete Multiple Vendors"
        message={`Are you sure you want to delete ${checkedItems.length} selected vendors? This action cannot be undone.`}
      />
    </div>
  )
}

export {Archive}
